import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { lowerCase } from 'lodash';
import { APP_CONFIG } from '@constants';
import { getObjectValue, safeJsonParse } from '@utility/helperFunction';
import { runWhenIdle } from '@helpers/generic';
import store from '@store';
import { checkOpen } from '@reducers/checks';
import {
  getSeat,
  setSeat,
  setCurrentPage,
  setPosition,
  removePosition,
  clearCart,
  changeCartLoadingStatus,
  pushCartItems,
  setOrderNavLoading,
  setSpeedOrderStatus,
  setPaymentCheck,
} from '@reducers/orders';
import { orderFinalize, driveSplitCheck } from '@reducers/payment';
import { updateChecksAfterResponse } from '@reducers/storeChecks';
import { setErrorPopUp, setNotification } from '@reducers/notification';
import {changeMenu} from '@reducers/orders'
import getDeviceType from '@utility/deviceType';

const SOURCE_TYPES = {
  DEFAULT: 0,
  COUNTER: 9,
  CALL_CENTER: 10,
  SPEED_ORDER: 11,
  DRIVE_THRU: 12,
  TIP_ONLY: 13,
};

export const useSubmitTableNumber = () => {
  const navigate = useNavigate();

  return useCallback(
    (tableValue) => {
      const { oauth } = store.getState();
      const { deviceInformation, authInfo, loggedInUserData } = oauth;

      let station;
      authInfo?.stations?.forEach(item => {
        if (/[a-zA-Z]/.test(tableValue.tableNumber)) {
          if (
            item.number &&
            /[a-zA-Z]/.test(item.number) &&
            item.number.toLowerCase() === tableValue.tableNumber.toLowerCase()
          ) {
            station = item;
          }
        } else if (item.number === tableValue.tableNumber) {
          station = item;
        }
      });

      store
        .dispatch(
          checkOpen({
            deviceId: deviceInformation.deviceId,
            employeeId: Number(loggedInUserData?.id),
            storeId: deviceInformation.storeId,
            stationId: station?.id ?? null,
            name: null,
            stationGroupId: null,
            isTab: false,
            phoneNumber: null,
            pickupTime: null,
            guestName: null,
            guestCount: tableValue.guestNumber || 1,
            sourceType: 0,
            delivery: false,
            speedOrder: false,
            employeeCard: loggedInUserData?.employeeCard,
          })
        )
        .then(val => {
          store.dispatch(updateChecksAfterResponse(val));
          store.dispatch(
            setSeat({
              ...val,
              pendingItems: getObjectValue(
                () => val?.orders[0]?.items.filter(i => i.pending),
                []
              ),
            })
          );
          store.dispatch(removePosition());
          const position =
            val?.seats?.length > 0 ? val.seats[0] : { number: -1, position: -1 };
          store.dispatch(setPosition(position));
          store.dispatch(setCurrentPage(0));
          window.sessionStorage.removeItem('menuTypeId');
          window.sessionStorage.removeItem('menuScrollTop');
          navigate(`${APP_CONFIG.BASE_URL}/orders`, { replace: true });
        })
        .catch(err => {
          store.dispatch(setErrorPopUp({ res: err.message }));
        });
    },
    [navigate]
  );
};

const visibilityCheck = (userInfo, check, authInfo, employeeJobHashTable) => {
  if (!userInfo || !check || !authInfo) return false;

  const teamId = authInfo.jobs?.find(e => e.id === userInfo.jobId)?.jobTeamId;
  const jobList = authInfo.jobs?.filter(e => e.jobTeamId === teamId) ?? [];

  let employeeList = {};
  jobList.forEach(e => {
    if (
      employeeJobHashTable?.[e.id] &&
      employeeJobHashTable[e.id].employees?.[check.employeeId]
    ) {
      employeeList = employeeJobHashTable[e.id].employees;
    }
  });

  const checkEmployee = authInfo.employees?.find(e => e.id === check.employeeId);
  if (checkEmployee) {
    const jobId = checkEmployee.jobId;
    if (
      employeeJobHashTable?.[jobId] &&
      employeeJobHashTable[jobId].checkVisibility === 3
    ) {
      return true;
    }
  }

  if (!userInfo.jobId) {
    return userInfo.id === check.employeeId;
  }

  if (!employeeJobHashTable?.[userInfo.jobId]) {
    return userInfo.id === check.employeeId;
  }

  switch (employeeJobHashTable[userInfo.jobId].checkVisibility) {
    case 0:
      return userInfo.id === check.employeeId;
    case 1:
      return !teamId
        ? Object.keys(
          employeeJobHashTable[userInfo.jobId].employees ?? {}
        ).includes(`${check.employeeId}`)
        : Object.keys(employeeList).includes(`${check.employeeId}`);
    default:
      return true;
  }
};

export const useValidateTableNumber = ({
  onTableOpen,
  onGoToOrder,
  onShowConflictModal,
}) => {
  return useCallback((data) => {
    const { oauth, storeChecks } = store.getState();
    const stations = oauth.authInfo?.stations ?? [];
    const tableData = storeChecks.checkListHashTable ?? {};
    const userInfo = oauth.loggedInUserData;

    return new Promise((resolve, reject) => {
      if (!data.tableNumber || !data.tableNumber.trim()) {
        reject(new Error('Enter a Table Number'));
        return;
      }
      if (!data.tableNumber.match(/^[a-zA-Z0-9]*$/)) {
        reject(new Error('Table Number is required'));
        return;
      }
      const station = stations.find(
        item => item?.number?.toUpperCase() === data.tableNumber.toUpperCase()
      );
      if (!station) {
        reject(new Error('Table not found'));
        return;
      }
      const isOpen = Object.values(tableData).find(
        e =>
          e.open &&
          (e.stationName?.toLowerCase() === data.tableNumber.toLowerCase() ||
            e.stationNumber?.toLowerCase() === data.tableNumber.toLowerCase())
      );
      if (isOpen) {
        const isVisible = visibilityCheck(
          userInfo,
          isOpen,
          oauth.authInfo,
          oauth.employeeJobHashTable
        );
        if (isVisible) {
          if (isOpen?.employeeId === userInfo?.id) {
            onTableOpen(data);
          } else {
            onGoToOrder(isOpen);
          }
        } else {
          const employeData = oauth.employeeHashTable?.[isOpen.employeeId];
          onShowConflictModal(data, employeData);
        }
        reject(new Error(''));
        return;
      }
      resolve({});
    });
  }, [onTableOpen, onGoToOrder, onShowConflictModal]);
};

export const useSubmitTabName = (countryCode) => {
  const navigate = useNavigate();

  return useCallback(
    (tabValue = {}) => {
      const { oauth, storeChecks } = store.getState();
      const { deviceInformation, loggedInUserData } = oauth;
      const { isCounter, isDriveThru, isCallCenter } = getDeviceType();

      const isSpeedOrder = tabValue === 'speedOrder';
      const isTipOnlyEnabled = storeChecks.isTipOnlyEnabled ?? false;

      if (isSpeedOrder) {
        store.dispatch(setSpeedOrderStatus(true));
        window.sessionStorage.setItem('speedStatus', true);
      }

      const serverAppSpeedOrder =
        (!isCounter || !isDriveThru || isCallCenter) &&
        !!deviceInformation?.deviceGroup?.tableTypes?.speedOrder;

      const sourceType =
        [
          [isTipOnlyEnabled, SOURCE_TYPES.TIP_ONLY],
          [isSpeedOrder, SOURCE_TYPES.SPEED_ORDER],
          [isDriveThru, SOURCE_TYPES.DRIVE_THRU],
          [isCallCenter, SOURCE_TYPES.CALL_CENTER],
          [isCounter, SOURCE_TYPES.COUNTER],
        ].find(([condition]) => condition)?.[1] ?? SOURCE_TYPES.DEFAULT;

      store
        .dispatch(
          checkOpen({
            deviceId: deviceInformation?.deviceId,
            employeeId: Number(loggedInUserData?.id),
            storeId: deviceInformation?.storeId,
            stationId: null,
            employeeCard: loggedInUserData?.employeeCard,
            name: isCounter || isDriveThru || !tabValue.tabName ? null : tabValue.tabName,
            stationGroupId: null,
            isTab: true,
            phoneNumber: null,
            pickupTime: null,
            guestName: null,
            guestCount: tabValue.guestNumber || 1,
            sourceType,
            delivery: false,
            speedOrder: isSpeedOrder ? serverAppSpeedOrder : false,
            countryCode,
          })
        )
        .then(val => {
          store.dispatch(updateChecksAfterResponse(val));
          store.dispatch(
            setSeat({
              ...val,
              pendingItems: getObjectValue(
                () => val?.orders[0]?.items.filter(i => i.pending),
                []
              ),
            })
          );
          store.dispatch(removePosition());
          const position =
            val?.seats?.length > 0 ? val.seats[0] : { number: -1, position: -1 };
          store.dispatch(setPosition(position));
          store.dispatch(setCurrentPage(0));

          if (isTipOnlyEnabled) {
            const dataToSend = {
              deviceId: deviceInformation?.deviceId,
              employeeId: Number(loggedInUserData?.id),
              storeId: deviceInformation?.storeId,
              checkHeaderId: val?.id,
              name: val?.name,
              items: val?.items || [],
              subtotalAmount: val?.subtotalAmount || 0,
            };

            store.dispatch(orderFinalize(dataToSend))
              .then(resData => {
                const dataToSendToAdd = {
                  deviceId: deviceInformation?.deviceId,
                  employeeId: Number(loggedInUserData?.id),
                  checkHeaderId: resData?.id,
                  storeId: resData?.storeId,
                  unpaidAmount: resData?.unpaidAmount,
                  totalAmount: resData?.totalAmount,
                  paidAmount: resData?.paidAmount,
                  splitType: 0,
                };
                store.dispatch(driveSplitCheck(dataToSendToAdd))
                  .then(res => {
                    const syncedCheck = res?.check || resData;
                    let updatedData = {
                      ...syncedCheck,
                      control: res?.control,
                      checkHeaderId: res?.checkHeaderId,
                      storeId: res?.storeId,
                      giftCard: res?.giftCard,
                      offer: res?.offer,
                      paymentCheck: res?.paymentCheck,
                      paymentChecks: res?.paymentChecks,
                      availableLoyaltyOffers: res?.availableLoyaltyOffers,
                      check: res?.check,
                    };
                    store.dispatch(setSeat(updatedData));
                    store.dispatch(setPaymentCheck(updatedData));
                    navigate(
                      `${APP_CONFIG.BASE_URL}/receipt/${res?.paymentCheck?.id}?ticketSplitTypeId=1&splitValue=1&screenType=4&type=payType`,
                      { replace: true }
                    );
                  })
                  .catch(err => {
                    store.dispatch(setErrorPopUp({ res: err.message }));
                  });
              })
              .catch(err => {
                store.dispatch(setErrorPopUp({ res: err.message }));
              });
          } else {
            window.sessionStorage.removeItem('menuTypeId');
            window.sessionStorage.removeItem('menuScrollTop');
            store.dispatch(clearCart()).then(() => {
              navigate(`${APP_CONFIG.BASE_URL}/orders`, { replace: true });
            });
          }
        })
        .catch(err => {
          store.dispatch(setErrorPopUp({ res: err.message }));
        });
    },
    [navigate, countryCode]
  );
};

export const useValidateTabName = ({ onTabOpen, onGoToOrder }) => {
  return useCallback(
    (data) => {
      const { oauth, storeChecks } = store.getState();
      const { loggedInUserData, deviceInformation } = oauth;
      const tableData = storeChecks.checkListHashTable ?? {};
      const { isCounter, isDriveThru } = getDeviceType();
      const nameRequired = deviceInformation?.deviceGroup?.inputFields?.nameRequired;

      return new Promise((resolve, reject) => {
        if (isCounter || isDriveThru) {
          if (nameRequired && (!data.tabName || !data.tabName.trim())) {
            reject(new Error('Name is required'));
            return;
          }
          resolve({ isCheckModalOpenFlag: false });
          return;
        }

        if (!data.tabName || !data.tabName.trim()) {
          reject(new Error('Please enter a tab name'));
          return;
        }

        const existingTab = Object.values(tableData).find(
          e =>
            e.open &&
            e.tab &&
            (e.stationName === data.tabName || e.stationNumber === data.tabName)
        );

        if (existingTab) {
          const isVisible = visibilityCheck(
            loggedInUserData,
            existingTab,
            oauth.authInfo,
            oauth.employeeJobHashTable
          );
          if (isVisible) {
            if (Number(existingTab.employeeId) === Number(loggedInUserData?.id)) {
              onTabOpen(data);
            } else {
              onGoToOrder(existingTab);
            }
          } else {
            store.dispatch(
              setErrorPopUp({ res: `Tab is already open`, noErrorTxt: true })
            );
          }
          reject(new Error(''));
          return;
        }

        resolve({ isCheckModalOpenFlag: false });
      });
    },
    [onTabOpen, onGoToOrder]
  );
};

function formatPickupTime(time) {
  if (!time) return '';
  const [clockPart, period] = time.split(' ');
  const hourStr = clockPart.substring(0, 2);
  const rest = clockPart.substring(2);
  if (period === 'PM') {
    return hourStr !== '12' ? String(Number(hourStr) + 12) + rest : clockPart;
  }
  if (hourStr === '12') return Number(hourStr) - 12 + '0' + rest;
  return clockPart;
}

export const useValidateTogo = () =>
  useCallback(({ name, phone, countryCode: cc, pickup }) => {
    const phoneDigits = phone?.replace(/-/g, '') ?? '';

    if (!name) {
      store.dispatch(setErrorPopUp({ res: 'Name is required', noErrorTxt: true }));
      return false;
    }
    if (!phone) {
      store.dispatch(setErrorPopUp({ res: 'Phone number is required', noErrorTxt: true }));
      return false;
    }
    if (cc === '1' && phoneDigits.length !== 10) {
      store.dispatch(setErrorPopUp({ res: 'Phone number length must be 10 digits', noErrorTxt: true }));
      return false;
    }
    if (cc !== '1' && phoneDigits.length > 15) {
      store.dispatch(setErrorPopUp({ res: 'Phone number length must be 15 digits', noErrorTxt: true }));
      return false;
    }
    if (!pickup) {
      store.dispatch(setErrorPopUp({ res: 'PickUp time is required', noErrorTxt: true }));
      return false;
    }
    return true;
  }, []);

export const useSubmitTogo = () => {
  const navigate = useNavigate();

  return useCallback(
    ({ name, phone, countryCode: cc, pickup }) => {
      const { oauth } = store.getState();
      const { deviceInformation } = oauth;
      const userInfo = safeJsonParse(window.sessionStorage.getItem('userInfo'), {});
      const employeeId = Number(userInfo.id);
      const employeeCard = getObjectValue(() => userInfo.employeeCard, null);

      const createPayload = {
        deviceId: deviceInformation?.deviceId,
        employeeId,
        storeId: deviceInformation?.storeId,
        phoneNumber: phone,
        countryCode: cc,
        name,
        pickupTime: formatPickupTime(pickup),
        stationId: null,
        toGo: true,
        employeeCard,
        stationGroupId: null,
        isTab: true,
        guestName: null,
        guestCount: 1,
        sourceType: 8,
        delivery: false,
        speedOrder: false,
      };

      store
        .dispatch(checkOpen(createPayload))
        .then(val => {
          createPayload.pickupTime = [pickup];
          window.sessionStorage.setItem(
            'callCenterUserInfo',
            JSON.stringify(createPayload)
          );
          store.dispatch(
            setSeat({
              ...val,
              pendingItems: (val.orders?.[0]?.items ?? [])
                .filter(e => e.pending)
                .map(e => ({ ...e, ...JSON.parse(e.orderJson) })),
            })
          );
          const position = val?.seats?.find(e => e.open) ?? {
            number: -1,
            position: -1,
          };
          store.dispatch(setPosition(position));
          window.sessionStorage.removeItem('menuTypeId');
          window.sessionStorage.removeItem('menuScrollTop');
          navigate(`${APP_CONFIG.BASE_URL}/orders`, { replace: true });
        })
        .catch(err => {
          store.dispatch(setNotification({ type: 'danger', message: err.message }));
        });
    },
    [navigate]
  );
};

export const useGoToOrderFromSearch = () => {
  const navigate = useNavigate();

  return useCallback(
    (data) => {
      store.dispatch(setOrderNavLoading(true));
      Promise.all([
        store.dispatch(getSeat(data)),
        store.dispatch(clearCart()),
        store.dispatch(changeCartLoadingStatus({ isCartLoading: true })),
      ]).then(() => {
        store.dispatch(removePosition());
        const cartItems = getObjectValue(() => data?.orders[0]?.items, [])
          .filter(e => e.pending)
          .map(it => {
            const order = safeJsonParse(it.orderJson, {});
            const modifier = order.modifierJson
              ? safeJsonParse(order.modifierJson, {})
              : {};
            return {
              ...modifier,
              orderJson: it.orderJson,
              modifierJson: order.modifierJson,
              id: it.id,
              pending: true,
              refill: order.refill,
            };
          });
        store.dispatch(pushCartItems(cartItems));
        const position =
          data?.seats?.filter(e => e.open).length > 0
            ? data.seats.find(e => e.open)
            : { number: -1, position: -1 };
        store.dispatch(setPosition(position));
        window.sessionStorage.removeItem('menuTypeId');
        window.sessionStorage.removeItem('menuScrollTop');
        runWhenIdle(() => {
          store.dispatch(changeCartLoadingStatus({ isCartLoading: false }));
        });
        window.sessionStorage.removeItem('menuIndex');
        window.sessionStorage.removeItem('menuName');
        store.dispatch(changeMenu(0));
        setTimeout(() => navigate(`${APP_CONFIG.BASE_URL}/orders`));
      });
    },
    [navigate]
  );
};

export const useHeaderSearch = (tableData) => {
  const [isSearch, setIsSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const searchUpdated = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const openSearch = useCallback(() => {
    setIsSearch(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearch(false);
    setSearchQuery('');
  }, []);

  const filteredTableData = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return tableData;
    const query = lowerCase(searchQuery);
    const result = {};
    Object.entries(tableData ?? {}).forEach(([key, check]) => {
      const stationName = lowerCase(check?.stationName ?? '');
      const stationNumber = lowerCase(String(check?.stationNumber ?? ''));
      const number = lowerCase(String(check?.number ?? ''));
      const employeeName = lowerCase(
        `${check?.employeeFirstName ?? ''} ${check?.employeeLastName ?? ''}`.trim()
      );
      if (
        stationName.includes(query) ||
        stationNumber.includes(query) ||
        number.includes(query) ||
        employeeName.includes(query)
      ) {
        result[key] = check;
      }
    });
    return result;
  }, [tableData, searchQuery]);

  return { isSearch, searchQuery, searchUpdated, openSearch, closeSearch, filteredTableData };
};

